/**
 * Right pane of the Appeal Info modal: single-screen appeal form (no Call/Fax tabs).
 */

import { useState } from "react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { Button, Select, TextInput } from "risa-oasis-ui_v2";
import {
  APPEAL_STATUS_OPTIONS,
  APPEAL_TYPE_OPTIONS,
} from "../../../../constants/appealInfoOptions";
import { CaseAppealsTableRowData } from "../../../../data-model/caseAppealsTable";
import { onAppealInfoSave } from "../actionHandlers/onAppealInfoSave";
import {
  AppealInfoDataKeys,
  useAppealInfoData,
} from "../hooks/useAppealInfoData";

const APPEAL_TYPE_SELECT_OPTIONS = APPEAL_TYPE_OPTIONS.map((option) => ({
  label: option.text,
  value: option.id,
}));

const APPEAL_STATUS_SELECT_OPTIONS = APPEAL_STATUS_OPTIONS.map((option) => ({
  label: option.text,
  value: option.id,
}));

/**
 * Splits the footer evenly between Cancel and Save. The oasis `Button` takes no
 * className, so the rendered element is stretched from its wrapper.
 */
const FOOTER_BUTTON_WRAPPER_CLASSES = "flex-1 [&>button]:w-full";

/**
 * Lifts the oasis field labels one step above the library default, matching
 * the Non-EPA Call Summary field styling.
 */
const FIELD_LABEL_CLASSES = [
  "[&_label]:!text-small [&_label]:!leading-small [&_label]:!text-primaryGray-3",
  "[&_.label]:!text-small [&_.label]:!leading-small [&_.label]:!text-primaryGray-3",
].join(" ");

/**
 * Comment box styled like an oasis text input at a taller wrapping height.
 */
const COMMENT_INPUT_CLASSES = [
  "h-20 w-full resize-none rounded border border-solid border-primaryGray-14",
  "px-3 py-2 font-[inherit] text-small text-primaryGray-1 outline-none",
  "placeholder:text-tiny placeholder:text-primaryGray-7",
  "focus:border-primaryGray-6",
].join(" ");

interface AppealInfoPaneProps {
  data: CaseAppealsTableRowData;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Single-screen Appeal Info form (type, status, reason, comments) with a
 * Cancel/Save footer matching the Non-EPA modal right pane, without Call/Fax tabs.
 *
 * @param props.data - Appeal table row used to prefill the form
 * @param props.onClose - Closes the modal
 * @param props.onSuccess - Called after a successful save so the table refetches
 * @returns Appeal Info form pane
 */
const AppealInfoPane = (props: AppealInfoPaneProps) => {
  const { appealInfoData, handleAppealInfoDataChange, isSaveEnabled } =
    useAppealInfoData(props.data);

  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);
    await onAppealInfoSave({
      data: props.data,
      appealInfo: appealInfoData,
      onSuccess: () => {
        props.onSuccess?.();
        props.onClose();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update appeal info");
      },
    });
    setIsLoading(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className={`grid grid-cols-2 gap-x-6 gap-y-5 ${FIELD_LABEL_CLASSES}`}>
          <Select
            id="appeal-type-select"
            label="Appeal Type"
            options={APPEAL_TYPE_SELECT_OPTIONS}
            placeholder="Select appeal type"
            required
            value={appealInfoData.appealType || undefined}
            onOptionChange={(option) =>
              handleAppealInfoDataChange(
                AppealInfoDataKeys.APPEAL_TYPE,
                option.value,
              )
            }
          />

          <Select
            id="appeal-status-select"
            label="Appeal Status"
            options={APPEAL_STATUS_SELECT_OPTIONS}
            placeholder="Select appeal status"
            required
            value={appealInfoData.appealStatus || undefined}
            onOptionChange={(option) =>
              handleAppealInfoDataChange(
                AppealInfoDataKeys.APPEAL_STATUS,
                option.value,
              )
            }
          />

          <div className="col-span-2">
            <TextInput
              id="appeal-reason-input"
              label="Appeal Reason"
              defaultValue={appealInfoData.appealReason}
              onChange={(event) =>
                handleAppealInfoDataChange(
                  AppealInfoDataKeys.APPEAL_REASON,
                  event.value,
                )
              }
            />
          </div>

          <div className="col-span-2 flex flex-col gap-1">
            <label htmlFor="appeal-comments-input">Appeal Comments</label>
            <textarea
              id="appeal-comments-input"
              name="appeal-comments-input"
              rows={3}
              placeholder="Enter appeal comments"
              defaultValue={appealInfoData.appealComments}
              onChange={(event) =>
                handleAppealInfoDataChange(
                  AppealInfoDataKeys.APPEAL_COMMENTS,
                  event.target.value,
                )
              }
              className={COMMENT_INPUT_CLASSES}
            />
          </div>
        </div>
      </div>

      <div className="flex w-full shrink-0 items-center gap-4 border-t border-primaryGray-15 px-6 py-4">
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="secondary"
            size="medium"
            onClick={props.onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
        <div className={FOOTER_BUTTON_WRAPPER_CLASSES}>
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={!isSaveEnabled || isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppealInfoPane;
